/**
 * OWNER-OPERATING-SYSTEM-V1 -- Source to revenue (/dashboard/reports/
 * source-to-revenue).
 *
 * ARCHETYPE: REPORT DRILL (F6b; K-27). "Which sources produce collected
 * revenue -- and how much money is honestly unlinked?" Composes the
 * EXISTING funnel snapshot's attribution facts (lib/revenue-attribution
 * via the owner funnel wire): per-source revenue attribution cells, the
 * recorded-collections identity (total = attributed + not linked, BY
 * CONSTRUCTION), and the NOT-LINKED row as a FIRST-CLASS fact with its
 * Why explainer -- never a footnote, never redistributed.
 *
 * The drill context bar states the window and grain (EVIDENCE-ROWS law);
 * money renders via the wire's own formatter contract (T-19 posture).
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { fetchOwnerFunnelSnapshot } from '@/dashboard/api';
import type {
  OwnerFunnelRangePreset,
  OwnerFunnelSnapshot,
} from '@/dashboard/owner/owner-funnel-view';
import {
  formatCount,
  formatInr,
  revenueCellText,
} from '@/dashboard/owner/owner-funnel-view';
import {
  DataTable,
  EmptyState,
  KpiBand,
  LoadingState,
  PageHeader,
  SeamChip,
  SectionBand,
  SelectorBar,
  type DataTableColumn,
  type SelectorControlDef,
} from '@/dashboard/ui';

const RANGE_OPTIONS: readonly { value: OwnerFunnelRangePreset; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '3m', label: 'Last 3 months' },
  { value: '6m', label: 'Last 6 months' },
];

type SourceRow = OwnerFunnelSnapshot['sourceTable'][number];

export default function SourceToRevenuePage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const rangeParam = searchParams.get('range');
  const range: OwnerFunnelRangePreset =
    rangeParam === '7d' || rangeParam === '3m' || rangeParam === '6m' ? rangeParam : '30d';

  const [snapshot, setSnapshot] = useState<OwnerFunnelSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchOwnerFunnelSnapshot({ range })
      .then((data) => {
        if (!cancelled) {
          setSnapshot(data);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Load failed');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [range]);

  const controls: SelectorControlDef[] = useMemo(
    () => [
      {
        key: 'range',
        label: 'Date range',
        options: RANGE_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
        value: range,
        onChange: (v) => {
          const next = new URLSearchParams(searchParams);
          next.set('range', v);
          setSearchParams(next, { replace: true });
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [range, searchParams],
  );

  const columns: DataTableColumn<SourceRow>[] = useMemo(() => {
    if (!snapshot) return [];
    return [
      { key: 'source', header: 'Source', render: (r) => <strong>{r.sourceLabel}</strong> },
      { key: 'leads', header: 'Leads', align: 'end', render: (r) => formatCount(r.leads.value) },
      {
        key: 'revenue',
        header: 'Collected revenue',
        align: 'end',
        render: (r) => revenueCellText(r.revenueAttribution, snapshot.stateCopy),
      },
    ];
  }, [snapshot]);

  return (
    <div>
      <PageHeader
        title="Source to revenue"
        breadcrumbs={[{ label: 'Reports', to: '/dashboard/reports' }, { label: 'Source to revenue' }]}
        subs={[
          snapshot
            ? `${snapshot.range.from} to ${snapshot.range.to} -- collected active payments joined to first-touch source; unlinked money shown, never redistributed`
            : undefined,
        ]}
      />
      <div style={{ margin: '0 0 var(--ac-space-4)' }}>
        <SelectorBar controls={controls} testId="s2r-selbar" />
      </div>

      {loading ? <LoadingState label="Loading source to revenue" lines={3} /> : null}
      {error !== null && !loading ? (
        <EmptyState
          title="Source to revenue did not load."
          body={`${error} -- reload the page to retry.`}
        />
      ) : null}

      {snapshot ? (
        <div className="ac-band-flow">
          {snapshot.recordedCollections ? (
            <KpiBand
              title="The money identity"
              basis="total = attributed + not linked, by construction"
              cells={[
                {
                  key: 'total',
                  label: snapshot.recordedCollections.total.label,
                  value: formatInr(snapshot.recordedCollections.total.value),
                  meta: `${snapshot.recordedCollections.transactionCount} payments`,
                },
                {
                  key: 'attributed',
                  label: 'Attributed to a source',
                  value: formatInr(snapshot.recordedCollections.attributedInr),
                  meta: 'first-touch source on the paying patient',
                },
                {
                  key: 'not-linked',
                  label: 'Not linked',
                  value: formatInr(snapshot.recordedCollections.notLinkedInr),
                  meta: 'first-class fact -- see why below',
                },
              ]}
              testId="s2r-identity-band"
            />
          ) : (
            <SectionBand title="The money identity" testId="s2r-identity-seam">
              <p className="ac-opsrow">
                <SeamChip
                  label="Payments spine not connected for this window"
                  explainer={snapshot.stateCopy.revenueNotConnected}
                />
              </p>
            </SectionBand>
          )}

          <SectionBand
            title="Sources"
            basis={`${snapshot.sourceTable.length} sources in window`}
            testId="s2r-sources"
          >
            <DataTable
              columns={columns}
              rows={snapshot.sourceTable}
              rowKey={(r) => r.sourceNormalized}
              stickyHeader
              countLine={`${snapshot.sourceTable.length} sources -- revenue cells state their own truth ('${snapshot.stateCopy.revenueNotConnected}' where the join is not live)`}
              empty={
                <EmptyState
                  title="No sources in this window."
                  body="Rows appear once leads with sources exist in the selected range."
                />
              }
              testId="s2r-source-table"
            />
          </SectionBand>

          <SectionBand title="Not linked -- why" testId="s2r-why">
            <p className="ac-opsrow">
              {snapshot.revenueNotLinked ? (
                <SeamChip
                  label={`${formatInr(snapshot.revenueNotLinked.value)} collected without a linked lead`}
                  explainer={snapshot.revenueNotLinked.tooltip}
                />
              ) : (
                <SeamChip
                  label="Nothing unlinked reported for this window"
                  explainer="When the payments spine reports collected money whose patient has no linked lead, the amount renders here as a first-class row."
                />
              )}
              <Link to="/dashboard/leads" className="ac-selbar__chip">
                Lead evidence rows
              </Link>
            </p>
          </SectionBand>
        </div>
      ) : null}
    </div>
  );
}
